import { Box, Checkbox, IconButton, Typography } from '@mui/material'
import { styled } from '@mui/system'
import i18n from 'i18next'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import AssetsButtons from './AssetsButtons'
import { icons } from '../../../../assets'
import { getMediaUrl, getSimpleLang } from '../../../../helpers'
import { useDefaultComponentTools } from '../../../../hooks/useDefaultComponentTools'
import { setFavoriteAsset } from '../../../../store/redusers/assets/actions'
import {
	selectAssetBookmarked,
	selectAssetTitle,
	selectAssetId,
	selectAssetMuted,
	selectAssetNearestStart,
	selectAssetAlias
} from '../../../../store/redusers/assets/selectors'
import { setShowLoginPopup } from '../../../../store/redusers/auth/actions'
import { selectAccessToken } from '../../../../store/redusers/auth/selectors'
import { setOrderTradeSellBuy } from '../../../../store/redusers/orders/actions'
import { getNewsData } from '../../../../store/redusers/positions/actions'
import { selectUserId } from '../../../../store/redusers/user/selectors'
import AppIconDouble from '../../../AppIconDouble'
import AppInfoIcon from '../../../AppInfoIcon'

const AssetsItemBody = styled(Box)(({theme, active, muted}) => ({
	'&:hover': {background: theme.palette.lineHover,
		cursor: muted ? 'not-allowed' : 'pointer'},
	alignItems: 'center',
	background: active
		&& theme.palette.lineHover,
	borderBottom: theme.palette.border,
	cursor: 'pointer',
	display: 'flex',
	gap: '4px',
	height: '56px',
	transition: '0.5s',
	[theme.breakpoints.down('md')]: {
		height: '45px'
	}
}))

const AssetsItem = ({
	assetSoId,
	onHandleClickAsset,
	active,
	assetType
}) => {

	const { dispatch, alert, theme, mdDown, lgDown, mdUp, t,smDown } = useDefaultComponentTools()
	const assetTitle = useSelector(selectAssetTitle(assetType, assetSoId)) || ''
	const assetId = useSelector(selectAssetId(assetType, assetSoId))
	const bookmarked = useSelector(selectAssetBookmarked(assetType, assetSoId))
	const justFirstInit = useRef(false)
	const isDouble = assetType === 'currencies'
	const accessToken = useSelector(selectAccessToken)
	const isAuthorized = useSelector(selectUserId)
	const muted = useSelector(selectAssetMuted(assetType, assetSoId)) || ''
	const nearestStart = useSelector(selectAssetNearestStart(assetType, assetSoId)) || ''
	const alias = useSelector(selectAssetAlias(assetType, assetSoId))
	const [localBookmarked, setLocalBookmarked] = useState(bookmarked)

	useEffect(() => {
		setLocalBookmarked(bookmarked)
	}, [bookmarked])

	const onFavorite = async (e) => {
		e.stopPropagation()
		e.preventDefault()
		if (!isAuthorized) {
			dispatch(setShowLoginPopup(true))
			return
		}
		justFirstInit.current = true
		setLocalBookmarked(!localBookmarked)
		try {
			const res = await dispatch(setFavoriteAsset(
				{assetSoId, assetType},
				accessToken,
				`${
					'&assetId=' + assetId
					+'&favorite=' + !bookmarked
				}`))
			if(res) {
				alert.success(t('Favorite Assets Were Updated'), {timeout: 3000})
			}
		} catch (e) {
			setLocalBookmarked(!!localBookmarked)
			alert.removeAll()
			'message' in e && alert.error(e.message)
		}
	}

	const onGetNews = () => {
		if (assetType === 'stocks' && alias)	dispatch(getNewsData(getSimpleLang(i18n.language) || 'en', alias))
	}
	const onClickAsset = (e, type, action) => {
		e.stopPropagation()
		onHandleClickAsset(assetSoId, assetTitle, assetType, type)

		dispatch(setOrderTradeSellBuy(action))
		onGetNews()
	}
	const mediaUrl = getMediaUrl()

	return (
		<AssetsItemBody
			onClick={muted ? onGetNews : (e) => onClickAsset(e, 'type_asset', '')}
			active={active ? 1 : 0}
			muted={muted  ? 1 : 0}
		>
			<Box sx={{p: mdDown ? '0 8px' : '0 16px'}}>
				<AppIconDouble
					name={assetTitle}
					double={isDouble}
					size={mdDown ? 18 : 24}
					positionTop={mdDown ? '-11px' : '-17px'}
					sxCustom={{width: '18px'}}
				/>
			</Box>
			<Box sx={{width: mdUp || smDown ? 'auto' : '34%' }}>
				<Typography noWrap sx={{color: theme.palette.text.primary,
					overflow: 'hidden',	textOverflow: 'ellipsis',
					width: mdUp || smDown ? (lgDown  ? '90px' : '108px') : '100%' }}
				>
					{assetTitle}
				</Typography>
			</Box>
			<IconButton sx={{':hover': {opacity: 0.8}}}
				onClick={(e) => onClickAsset(e, 'type_asset_info', t('Asset Infо'), '')}
			>
				<AppInfoIcon/>
			</IconButton>
			{muted ?
				<Box sx={{ alignItems: 'center', background: theme.palette.second,
					borderRadius: '4px', display: 'flex', justifyContent: 'center',
					minHeight: '32px', width: '50%'}}
				>
					<Typography sx={{textAlign: 'center'}}>{t('Will be available on') + nearestStart}</Typography>
				</Box>
				: <Box sx={{width: '50%'}}>
					<AssetsButtons clickAsset={onClickAsset} assetSoId={assetSoId}/>
				</Box>
			}
			<Box sx={{ }}>
				<Checkbox
					checked={!!localBookmarked}
					onClick={onFavorite}
					icon={<img src={mediaUrl + icons.assetStarCheck} alt={'star-check'} />}
					checkedIcon={<img src={mediaUrl + icons.assetStarChecked} alt={'star'} />} />
			</Box>
		</AssetsItemBody >
	)
}

AssetsItem.propTypes = {
	active: PropTypes.bool,
	assetSoId: PropTypes.string,
	assetType: PropTypes.string,
	onHandleClickAsset: PropTypes.func
}

AssetsItem.displayName = 'AssetsItem'

export default AssetsItem
